import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  DataFormat,
  IWorkdayProjectDatasetsClient,
  WorkdayDatasetDto,
  SortDirections,
  SorterDescriptor,
  WorkdayProjectDatasetsClient,
  UpdateWorkdayDatasetStatusCommand,
} from "app/ApiClient";
import { IState } from "slices";
import { LoadingState } from "types";

import {
  initialWorkdayProjectDatasetsState,
  PageContextOfWorkdayProjectDatasetFilter,
  WorkdayProjectDatasetsState,
  WorkdayProjectGroupedDataset,
  WorkdayProjectDataset,
} from "./workdayProjectDatasetsState";
import { CUSTOMDATA, stackedProjectRegExp } from "hooks";
import { WorkdayProjectDatasetsTableConfig } from "const/workdayProjectDatasets";
const workdayProjectDatasetsClient: IWorkdayProjectDatasetsClient = new WorkdayProjectDatasetsClient(
  process.env.REACT_APP_API_URL,
);

const setDataFormatAndProjectId = (datasets: WorkdayDatasetDto[]): WorkdayProjectDataset[] => {
  return datasets.map((item) => {
    const isStackedProject = item.originProjectId && stackedProjectRegExp.test(item.originProjectId);
    const isCustomProject = item.originSource === CUSTOMDATA;
    return {
      ...item,
      dataFormat: !isStackedProject ? DataFormat.Flat.toString() : DataFormat.Stacked.toString(),
      normalizedOriginProjectId: isCustomProject ? item.workdayId : item.originProjectId,
    };
  });
};

const groupDatasets = (workdayProjectDatasets: WorkdayProjectDataset[]): WorkdayProjectGroupedDataset[] => {
  const workdayProjectGroupedDatasets: WorkdayProjectGroupedDataset[] = [];

  const flatDatasets = workdayProjectDatasets.filter((item) => item.dataFormat === DataFormat.Flat);
  const stackedDatasets = workdayProjectDatasets.filter((item) => item.dataFormat === DataFormat.Stacked);

  flatDatasets.forEach((item) => {
    const workdayProjectGroupedDataset: WorkdayProjectGroupedDataset = {
      parentDataset: item,
      childDatasets: [],
    };
    workdayProjectGroupedDatasets.push(workdayProjectGroupedDataset);
  });

  stackedDatasets.forEach((stackedDataset) => {
    const existingParentDataset = workdayProjectGroupedDatasets.find(
      (item) =>
        item.parentDataset.originProjectId &&
        item.parentDataset?.dataFormat === DataFormat.Flat &&
        stackedDataset.originProjectId?.includes(item.parentDataset.originProjectId),
    );
    if (existingParentDataset) {
      existingParentDataset.childDatasets.push(stackedDataset);
    } else {
      const workdayProjectGroupedDataset: WorkdayProjectGroupedDataset = {
        parentDataset: stackedDataset,
        childDatasets: [],
      };
      workdayProjectGroupedDatasets.push(workdayProjectGroupedDataset);
    }
  });

  return workdayProjectGroupedDatasets;
};

const sortComparison = (sortDirection: SortDirections, dataA?: string, dataB?: string): number => {
  if (dataA && dataB) {
    return sortDirection === SortDirections.Asceding ? dataA.localeCompare(dataB) : dataB.localeCompare(dataA);
  }
  if (dataA && !dataB) {
    return sortDirection === SortDirections.Asceding ? 1 : -1;
  }
  if (!dataA && dataB) {
    return sortDirection === SortDirections.Asceding ? -1 : 1;
  }
  return 0;
};

const sortDatasets = (
  datasets: WorkdayProjectGroupedDataset[],
  sortList?: SorterDescriptor[],
): WorkdayProjectGroupedDataset[] => {
  if (sortList && sortList.length > 0) {
    let groupedDatasets: WorkdayProjectGroupedDataset[] = datasets;
    for (const sortDesc of sortList) {
      const sortKey = sortDesc.field as keyof WorkdayProjectDataset;
      const sortedDatasets = groupedDatasets.sort((a, b) => {
        const dataParentDatasetA = a.parentDataset[sortKey]?.toString();
        const dataParentDatasetB = b.parentDataset[sortKey]?.toString();

        const sortDirection: SortDirections = sortDesc.directions ?? SortDirections.Desceding;

        return sortComparison(sortDirection, dataParentDatasetA, dataParentDatasetB);
      });

      sortedDatasets.forEach((group) => {
        group.childDatasets.sort((a, b) => {
          const dataChildDatasetA = a[sortKey]?.toString();
          const dataChildDatasetB = b[sortKey]?.toString();

          const sortDirection: SortDirections = sortDesc.directions ?? SortDirections.Desceding;

          return sortComparison(sortDirection, dataChildDatasetA, dataChildDatasetB);
        });
      });
      groupedDatasets = sortedDatasets;
    }
    return groupedDatasets;
  }
  return datasets;
};

export const updateWorkdayProjectDatasetStatus = createAsyncThunk(
  "workdayProjectDatasets/updateWorkdayProjectDatasetStatus",
  async (command: UpdateWorkdayDatasetStatusCommand, { dispatch, rejectWithValue }) => {
    try {
      await workdayProjectDatasetsClient.updateWorkdayDatasetStatus(command);
      await dispatch(getWorkdayProjectDatasetsByWorkdayProjectId({ workdayProjectId: command.workdayProjectId ?? 0, forceFetch: true }));
      return { status: command.status, command };
    } 
    catch (error) {
      console.error("Failed to update status:", error);
      return rejectWithValue(error);
    }
  }
);

export const getWorkdayProjectDatasetsByWorkdayProjectId = createAsyncThunk(
  "workdayProjectDatasets/getByWorkdayProjectId",
  async ({ workdayProjectId, forceFetch }: { workdayProjectId: number, forceFetch?: boolean }, { getState, rejectWithValue }) => {
    try {
      const { workdayProjectDatasets } = getState() as RootState;
      const { page: pageContext, filter } = workdayProjectDatasets.pageContext;

      let workdayProjectDatasetList: WorkdayProjectDataset[] =
        workdayProjectDatasets.workdayProjectGroupedDatasets.allData;
      if (!workdayProjectDatasetList?.length || forceFetch) {
        const datasets = await workdayProjectDatasetsClient.getByWorkdayProjectId(workdayProjectId);
        workdayProjectDatasetList = setDataFormatAndProjectId(datasets);
      }

      const searchString = filter?.searchString ?? "";
      const {
        pageNumber = WorkdayProjectDatasetsTableConfig.DefaultPageNumber,
        pageSize = WorkdayProjectDatasetsTableConfig.DefaultPageSize,
      } = pageContext ?? {};

      const workdayProjectDatasetFilters = (workdayProjectDatasets.filters as WorkdayProjectDataset) ?? {};

      let filteredData = workdayProjectDatasetList.filter((item) =>
        searchString
          ? [item.name ?? "", item.normalizedOriginProjectId ?? ""].some((value: string) => {
              if (value) {
                const stringValue = value.toString().toLowerCase();
                return stringValue.includes(searchString.toLowerCase());
              }
              return false;
            })
          : true,
      );

      if (Object.keys(workdayProjectDatasetFilters).length > 0) {
        filteredData = filteredData.filter((item: WorkdayProjectDataset) =>
          Object.keys(workdayProjectDatasetFilters).every((key) => {
            const filterValues = workdayProjectDatasetFilters[key as keyof WorkdayProjectDataset];
            if (Array.isArray(filterValues) && filterValues.length > 0) {
              const fieldValue = item[key as keyof WorkdayProjectDataset];
              const actualFieldValue = fieldValue == undefined ? "" : fieldValue;
              return filterValues.includes(actualFieldValue);
            }
            return true;
          }),
        );
      }
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      const workdayProjectGroupedDatasets = groupDatasets(filteredData);
      const sortedDatasets = sortDatasets(workdayProjectGroupedDatasets, workdayProjectDatasets.pageContext.sortList);
      let result = workdayProjectDatasets.workdayProjectGroupedDatasets;
      result = {
        allData: workdayProjectDatasetList,
        groupedData: sortedDatasets ?? [],
        pageData: [],
        pageNumber: workdayProjectDatasets.pageContext.page?.pageNumber,
        pageSize: workdayProjectDatasets.pageContext.page?.pageSize,
      };

      result = {
        ...result,
        totalCount: sortedDatasets.length,
        pageData: sortedDatasets.slice(startIndex, endIndex),
      };
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const workdayprojectdatasetsSlice = createSlice({
  name: "workdayprojectdatasets",
  initialState: initialWorkdayProjectDatasetsState,
  reducers: {
    resetWorkdayProjectDatasets: () => initialWorkdayProjectDatasetsState,
    setWorkdayProjectDatasetsPageContext: (
      state,
      { payload }: { payload: { pageContext: PageContextOfWorkdayProjectDatasetFilter; isPageChanged?: boolean } },
    ) => {
      const isPageChanged = payload.isPageChanged;
      const pageContext = payload.pageContext;
      state.pageContext = isPageChanged ? { ...state.pageContext, page: pageContext.page } : pageContext;
      state.filters = { ...state.filters };
    },

    setWorkdayProjectDatasetsFilters: (
      state,
      { payload }: { payload: { [key in keyof WorkdayProjectDataset]: string[] } },
    ) => {
      if (!state.pageContext.filter) state.pageContext.filter = {};
      state.filters = { ...state.filters, ...payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWorkdayProjectDatasetsByWorkdayProjectId.pending, (state) => {
      state.datasetsLoading = LoadingState.pending;
    });
    builder.addCase(getWorkdayProjectDatasetsByWorkdayProjectId.rejected, (state, action) => {
      state.datasetsLoading = LoadingState.failed;
      state.error = action.error.message;
    });
    builder.addCase(getWorkdayProjectDatasetsByWorkdayProjectId.fulfilled, (state, action) => {
      state.datasetsLoading = LoadingState.succeed;
      state.workdayProjectGroupedDatasets = action.payload;
    });
  },
});
export const { resetWorkdayProjectDatasets, setWorkdayProjectDatasetsPageContext, setWorkdayProjectDatasetsFilters } =
  workdayprojectdatasetsSlice.actions;

export const workdayProjectDatasetsSelector = (state: IState): WorkdayProjectDatasetsState =>
  state.workdayProjectDatasets;

export default workdayprojectdatasetsSlice.reducer;
